import { isEqual } from 'lodash/fp';
import { DateTime } from 'luxon';

import { Period } from 'dashboard/lib/unixTime';
import {
  CaseAnalyticsAssignmentDistribution,
  CaseAnalyticsIntervals,
  CaseAnalyticsResolutionDistribution,
  CaseAnalyticsResolutionsByTag,
  CaseAnalyticsResolutionTimes,
  CaseAnalyticsStatusDistributionCurrentUser,
  CaseAnalyticsTagDistribution,
} from 'dashboard/models/case-analytics';
import { CaseTemplate } from 'dashboard/models/case-template';
import {
  CaseAnalyticsActions,
  CaseAnalyticsActionType,
  CaseAnalyticsFilterDateType,
  CaseAnalyticsFilterParams,
} from 'dashboard/redux/actions/case-analytics';
import { Iso8601DateString } from 'lib/types';

const DEFAULT_CREATED_AT_START = DateTime.local().minus({ days: 14 }).startOf('day');
const DEFAULT_CREATED_AT_END = DateTime.local().endOf('day');

export const initialCaseAnalyticsFilterState: CaseAnalyticsFilterParams = {
  startDate: DEFAULT_CREATED_AT_START.toISO(),
  endDate: DEFAULT_CREATED_AT_END.toISO(),
  dateType: CaseAnalyticsFilterDateType.CreatedAt,
  caseStatus: [],
  caseTagIds: [],
  caseTemplateIds: [],
  caseStateIds: [],
  assigneeIds: [],
  teamAssigneeIds: [],
  resolverIds: [],
  teamResolverIds: [],
};

export interface CaseAnalyticsState {
  caseStatusOverTime: CaseAnalyticsIntervals;
  caseResolutionTimes: CaseAnalyticsResolutionTimes;
  caseAssignmentDistribution: CaseAnalyticsAssignmentDistribution;
  caseResolutionDistribution: CaseAnalyticsResolutionDistribution;
  caseTagDistribution: CaseAnalyticsTagDistribution;
  caseResolutionsByTag: CaseAnalyticsResolutionsByTag;
  caseStatusDistributionForCurrentUser: CaseAnalyticsStatusDistributionCurrentUser | undefined;
  filters: CaseAnalyticsFilterParams;
  caseTemplates: CaseTemplate[];
  /**
   * queriedAt is a separate state object because some of our other data are
   * stored as objects, which makes it hard to colocate. We also split out
   * queried at by UI tab.
   */
  queriedAt: {
    caseProductivity: Iso8601DateString | undefined;
    caseResolutions: Iso8601DateString | undefined;
  };
  updatedAt: Iso8601DateString | undefined;
}

export const initialState: CaseAnalyticsState = {
  caseStatusOverTime: {
    data: [],
    domain: [DEFAULT_CREATED_AT_START.toUnixInteger(), DEFAULT_CREATED_AT_END.toUnixInteger()],
    period: Period.Day,
  },
  caseResolutionTimes: {
    data: [],
    domain: [DEFAULT_CREATED_AT_START.toUnixInteger(), DEFAULT_CREATED_AT_END.toUnixInteger()],
    period: Period.Day,
  },
  caseAssignmentDistribution: {
    data: [],
  },
  caseResolutionDistribution: {
    data: [],
    domain: [DEFAULT_CREATED_AT_START.toUnixInteger(), DEFAULT_CREATED_AT_END.toUnixInteger()],
    period: Period.Day,
  },
  caseTagDistribution: {
    data: [],
    domain: [DEFAULT_CREATED_AT_START.toUnixInteger(), DEFAULT_CREATED_AT_END.toUnixInteger()],
    period: Period.Day,
  },
  caseResolutionsByTag: {
    data: [],
  },
  caseStatusDistributionForCurrentUser: {
    data: {
      count_by_state: {},
      count_resolved_today: undefined,
      count_sla_expired: undefined,
      count_sla_expires_next_hour: undefined,
    },
  },
  filters: initialCaseAnalyticsFilterState,
  caseTemplates: [],
  queriedAt: {
    caseProductivity: undefined,
    caseResolutions: undefined,
  },
  updatedAt: undefined,
};

// This is a function so we always get the most up-to-date created at times
const getInitialState = (): CaseAnalyticsState => ({
  ...initialState,
  caseStatusOverTime: {
    ...initialState.caseStatusOverTime,
    domain: [DEFAULT_CREATED_AT_START.toUnixInteger(), DEFAULT_CREATED_AT_END.toUnixInteger()],
  },
  caseResolutionTimes: {
    ...initialState.caseResolutionTimes,
    domain: [DEFAULT_CREATED_AT_START.toUnixInteger(), DEFAULT_CREATED_AT_END.toUnixInteger()],
  },
  filters: {
    ...initialState.filters,
    startDate: DEFAULT_CREATED_AT_START.toISO(),
    endDate: DEFAULT_CREATED_AT_END.toISO(),
  },
});

export default (
  state: CaseAnalyticsState = initialState,
  action: CaseAnalyticsActions,
): CaseAnalyticsState => {
  switch (action.type) {
    case CaseAnalyticsActionType.SetCaseAnalyticsIntervals: {
      return {
        ...state,
        caseStatusOverTime: action.payload.caseStatusOverTime,
        queriedAt: {
          ...state.queriedAt,
          caseProductivity: action.payload.queriedAt,
        },
        updatedAt: action.payload.updatedAt,
      };
    }

    case CaseAnalyticsActionType.SetCaseAnalyticsResolutionTimes: {
      return {
        ...state,
        caseResolutionTimes: action.payload.caseResolutionTimes,
        queriedAt: {
          ...state.queriedAt,
          caseProductivity: action.payload.queriedAt,
        },
      };
    }

    case CaseAnalyticsActionType.SetCaseAnalyticsAssignmentDistribution: {
      return {
        ...state,
        caseAssignmentDistribution: action.payload.caseAssignmentDistribution,
        queriedAt: {
          ...state.queriedAt,
          caseProductivity: action.payload.queriedAt,
        },
      };
    }

    case CaseAnalyticsActionType.SetCaseAnalyticsResolutionDistribution: {
      return {
        ...state,
        caseResolutionDistribution: action.payload.caseResolutionDistribution,
        queriedAt: {
          ...state.queriedAt,
          caseResolutions: action.payload.queriedAt,
        },
      };
    }

    case CaseAnalyticsActionType.SetCaseAnalyticsTagDistribution: {
      return {
        ...state,
        caseTagDistribution: action.payload.caseTagDistribution,
        queriedAt: {
          ...state.queriedAt,
          caseResolutions: action.payload.queriedAt,
        },
      };
    }

    case CaseAnalyticsActionType.SetCaseAnalyticsResolutionsByTag: {
      return {
        ...state,
        caseResolutionsByTag: action.payload.caseResolutionsByTag,
        queriedAt: {
          ...state.queriedAt,
          caseResolutions: action.payload.queriedAt,
        },
      };
    }

    case CaseAnalyticsActionType.SetCaseAnalyticsStatusDistributionForCurrentUser: {
      return {
        ...state,
        caseStatusDistributionForCurrentUser: action.payload.caseStatusDistributionForCurrentUser,
      };
    }

    case CaseAnalyticsActionType.SetCaseTemplates: {
      return {
        ...state,
        caseTemplates: action.payload.caseTemplates,
      };
    }

    case CaseAnalyticsActionType.ClearCaseAnalytics: {
      return { ...getInitialState(), filters: state.filters };
    }

    case CaseAnalyticsActionType.SetCaseAnalyticsFilters: {
      const newFilters = {
        ...state.filters,
        ...action.payload.filters,
      };

      if (isEqual(newFilters, state.filters)) {
        return state;
      } else {
        return {
          ...state,
          filters: newFilters,
        };
      }
    }

    case CaseAnalyticsActionType.ResetCaseAnalyticsDateFilter: {
      const initialState = getInitialState();

      return {
        ...state,
        filters: {
          ...state.filters,
          startDate: initialState.filters.startDate,
          endDate: initialState.filters.endDate,
          dateType: initialState.filters.dateType,
        },
      };
    }

    default:
      return state;
  }
};
